import { useState } from "react";
import { Lightbulb, Trash2, Send, CalendarPlus, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SuggestionItem, STATUS_STYLES } from "./SuggestionBox";

const INSERT_LABELS = {
  plaats: "Zet bij activiteiten",
  activiteit: "Zet bij activiteiten",
  restaurant: "Zet bij eten & drinken",
  verblijf: "Gebruik als overnachting",
  route: "Voeg toe als tussenstop & herbereken",
};

const InsertRow = ({ trip, s, onRefresh }) => {
  const needsDay = s.kind !== "route";
  const [day, setDay] = useState(s.day_number ?? "");
  const [busy, setBusy] = useState(false);
  if (s.kind === "anders") return null;
  if (s.inserted) {
    return (
      <p data-testid={`suggestion-inserted-chip-${s.id}`} className="mt-3 text-xs font-semibold text-[#2D6A4F] flex items-center gap-1.5">
        <Check className="w-3.5 h-3.5" /> Ingevoegd in de planning{s.inserted_day != null ? ` (dag ${s.inserted_day})` : ""} — {s.author_name} is per mail bedankt
      </p>
    );
  }
  const insert = async () => {
    if (needsDay && day === "") return toast.error("Kies eerst een dag");
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/suggestions/${s.id}/insert`, { day_number: needsDay ? Number(day) : null });
      toast.success(`Ingevoegd ${data.inserted_where} — ${s.author_name} krijgt een mailtje 🎉`);
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      {needsDay && (
        <select data-testid={`suggestion-insert-day-${s.id}`} value={day} onChange={(e) => setDay(e.target.value)}
          className="rounded-xl h-8 px-2.5 text-[11px] bg-secondary border-none font-semibold">
          <option value="">Kies dag…</option>
          {(trip.days || []).map((d) => (
            <option key={d.day_number} value={d.day_number}>{`Dag ${d.day_number} · ${d.date}`}</option>
          ))}
        </select>
      )}
      <button data-testid={`suggestion-insert-${s.id}`} onClick={insert} disabled={busy}
        className="rounded-full px-3.5 py-1.5 text-[11px] font-bold bg-[#2D6A4F] hover:bg-[#1B4332] text-white flex items-center gap-1.5 disabled:opacity-50 active:scale-95 transition-[transform,background-color] duration-200">
        <CalendarPlus className="w-3.5 h-3.5" /> {busy ? "Invoegen..." : INSERT_LABELS[s.kind]}
      </button>
    </div>
  );
};

const NoteRow = ({ trip, s, onRefresh }) => {
  const [note, setNote] = useState(s.owner_note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/trips/${trip.id}/suggestions/${s.id}`, { owner_note: note.trim() });
      toast.success("Reactie opgeslagen");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2 mt-3">
      <input data-testid={`suggestion-note-input-${s.id}`} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Korte reactie voor de gasten (optioneel)"
        className="flex-1 rounded-xl h-9 px-3 text-xs bg-secondary border-none" maxLength={500} />
      <button data-testid={`suggestion-note-save-${s.id}`} onClick={save} disabled={busy || note.trim() === (s.owner_note || "")}
        title="Reactie opslaan"
        className="h-9 w-9 rounded-xl bg-secondary hover:bg-secondary/70 flex items-center justify-center disabled:opacity-40 transition-[background-color] duration-200">
        <Send className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export const GuestSuggestions = ({ trip, onRefresh }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(`vr-sugg-collapsed-${trip.id}`) === "1"; } catch { return false; }
  });
  const suggestions = trip.suggestions || [];
  if (trip.is_owner === false || suggestions.length === 0) return null;
  const newCount = suggestions.filter((s) => s.status === "nieuw").length;
  const toggle = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem(`vr-sugg-collapsed-${trip.id}`, next ? "1" : "0"); } catch { /* noop */ }
    return next;
  });

  const setStatus = async (s, status) => {
    try {
      await api.patch(`/trips/${trip.id}/suggestions/${s.id}`, { status });
      toast.success(status === "toegevoegd" ? "Gemarkeerd als toegevoegd — de gast krijgt een mailtje" : status === "afgewezen" ? "Gemarkeerd als niet gepland — de gast krijgt een mailtje" : "Status bijgewerkt");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/trips/${trip.id}/suggestions/${s.id}`);
      toast.success("Suggestie verwijderd");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-[#3A6EA5]/5 rounded-2xl p-5 mb-6" data-testid="guest-suggestions-card">
      <button type="button" data-testid="suggestions-toggle" onClick={toggle} aria-expanded={!collapsed}
        title={collapsed ? "Suggesties uitklappen" : "Suggesties inklappen"}
        className="w-full text-left text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2 hover:text-foreground transition-[color] duration-200">
        <Lightbulb className="w-4 h-4 text-[#3A6EA5]" /> Suggesties van gasten ({suggestions.length})
        {newCount > 0 && (
          <span data-testid="suggestions-new-badge" className="min-w-[16px] h-4 px-1.5 rounded-full bg-[#3A6EA5] text-white text-[10px] font-bold inline-flex items-center justify-center">{newCount} nieuw</span>
        )}
        <ChevronDown className={`w-4 h-4 ml-auto shrink-0 transition-transform duration-200 ${collapsed ? "" : "rotate-180"}`} />
      </button>
      {!collapsed && (
      <div className="space-y-2.5 mt-4">
        {[...suggestions].reverse().map((s) => (
          <SuggestionItem key={s.id} s={s} days={trip.days || []}>
            <InsertRow trip={trip} s={s} onRefresh={onRefresh} />
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {["bekeken", "toegevoegd", "afgewezen"].map((st) => (
                <button key={st} data-testid={`suggestion-set-${st}-${s.id}`} onClick={() => setStatus(s, st)}
                  disabled={s.status === st}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition-[background-color,color] duration-200 ${s.status === st ? STATUS_STYLES[st].cls + " ring-1 ring-current" : "bg-secondary hover:bg-secondary/70"}`}>
                  {STATUS_STYLES[st].label}
                </button>
              ))}
              <button data-testid={`suggestion-delete-${s.id}`} onClick={() => remove(s)} title="Verwijder suggestie"
                className="ml-auto p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-[background-color,color] duration-200">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <NoteRow trip={trip} s={s} onRefresh={onRefresh} />
          </SuggestionItem>
        ))}
      </div>
      )}
    </div>
  );
};
